/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useTheme } from '../../context/ThemeContext';
import toast from 'react-hot-toast';
import { Scissors, Camera, Upload, Loader2, Info, X, Sparkles } from 'lucide-react';

// Components
import GenderToggle from '../../components/haircutsStudio/GenderToggle';
import HaircutPresetPanel from '../../components/haircutsStudio/HaircutPresetPanel';
import HairColorSelector from '../../components/haircutsStudio/HairColorSelector';
import BeforeAfterViewer from '../../components/haircutsStudio/BeforeAfterViewer';
import AnalysisPanel from '../../components/haircutsStudio/AnalysisPanel';
import CameraCapture from '../../components/haircutsStudio/CameraCapture';
import ClientSearchModal from '../../components/ClientSearchModal';

// Data & Utils
import { haircutPresets, getPresetsByGender, hairColorOptions, getHairColorById } from '../../data/haircutsStudioPresets';
import { resolveGenerationIntent, buildGenerationPayload, buildUserFriendlyPrompt } from '../../utils/haircutsStudioPromptBuilder';
import { hairstyleApiHelpers, handleHairstyleError, validateImageFile } from '../../services/hairstyleApi';
import { getHaircutRecommendations } from '../../services/hairRecommendationService';
import { buildHaircutStructure } from '../../services/haircutBuilderService';
import { updateClientWithHairstyleAnalysis } from '../../services/clientHairstyleApi';
import { normalizeFaceShape } from '../../constants/faceShapePromptSuggestions';
import { useCredits } from '../../hooks/useCredits';
import {
  clearHaircutsStudioSession,
  dataUrlToFile,
  loadHaircutsStudioSession,
  saveHaircutsStudioSession,
} from '../../utils/haircutsStudioSession';

// Brand accent color
const ACCENT = '#7d6633';
const MOBILE_VIEWPORT_QUERY = '(max-width: 767px)';
const waitForNextPaint = () => new Promise((resolve) => {
  requestAnimationFrame(() => requestAnimationFrame(resolve));
});

const HAIR_PROFILE_ENUMS = {
  curlType: ['straight', 'wavy', 'curly', 'coily', 'unknown'],
  density: ['low', 'medium', 'high', 'unknown'],
  strandThickness: ['fine', 'medium', 'thick', 'unknown'],
  hairline: ['normal', 'slightly_receding', 'receding', 'widow_peak', 'buzzed_or_not_visible', 'unknown'],
  currentLength: ['bald', 'buzzed', 'very_short', 'short', 'medium', 'long', 'very_long', 'unknown'],
  visibility: ['clear', 'partial', 'limited'],
};

const normalizeHairProfile = (hairProfile) => {
  if (!hairProfile || typeof hairProfile !== 'object') return null;

  const normalized = {};
  let hasAnyValue = false;

  Object.entries(HAIR_PROFILE_ENUMS).forEach(([key, allowed]) => {
    const raw = hairProfile[key];
    if (typeof raw !== 'string') return;
    const value = raw.trim().toLowerCase();
    if (!value) return;
    normalized[key] = allowed.includes(value)
      ? value
      : (key === 'visibility' ? 'limited' : 'unknown');
    hasAnyValue = true;
  });

  return hasAnyValue ? normalized : null;
};

const useIsMobileViewport = () => {
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_VIEWPORT_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mediaQuery = window.matchMedia(MOBILE_VIEWPORT_QUERY);
    const handleChange = (event) => setIsMobileViewport(event.matches);
    setIsMobileViewport(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isMobileViewport;
};

// Glass morphism Card component
const Card = React.forwardRef(({ children, className = '' }, ref) => (
  <div
    ref={ref}
    className={
      'rounded-2xl shadow-lg border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900/60 dark:backdrop-blur-xl ' +
      className
    }
  >
    {children}
  </div>
));
Card.displayName = 'Card';

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-zinc-200 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const HaircutsStudio = () => {
  const { user } = useAdminAuth();
  const { isDark } = useTheme();
  const { t } = useTranslation('admin');
  const { refetch: refetchCredits } = useCredits();
  const isMobileViewport = useIsMobileViewport();
  const fileInputRef = useRef(null);
  const resultCardRef = useRef(null);
  const clientSectionRef = useRef(null);
  const uploadSectionRef = useRef(null);
  const styleSectionRef = useRef(null);
  const sessionSaveWarningShownRef = useRef(false);
  const generationPhaseTimersRef = useRef([]);

  // ===== STATE =====
  // Gender & Presets
  const [gender, setGender] = useState(() => localStorage.getItem('hs_gender') || 'men');
  const [filteredPresets, setFilteredPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);

  // Hair Color
  const [selectedColorId, setSelectedColorId] = useState(() => {
    const mode = localStorage.getItem('hs_color_mode');
    return mode === 'no_change' ? 'no-change' : 'no-change';
  });

  // User Custom Prompt
  const [userCustomPrompt, setUserCustomPrompt] = useState('');

  // Reset function
  const handleResetSession = () => {
    clearHaircutsStudioSession();
    generationPhaseTimersRef.current.forEach(id => window.clearTimeout(id));
    generationPhaseTimersRef.current = [];
    setSelectedClient(null);
    setSourceImage(null);
    setSourceImageFile(null);
    setGender('men');
    setSelectedPreset(null);
    setSelectedColorId('no-change');
    setUserCustomPrompt('');
    setGeneratedImage(null);
    setUsedPrompt('');
    setSelectedStyle('');
    setVariationCount(0);
    setGenerationPhase(0);
    setDetectedFaceShape(null);
    setAnalysisData(null);
    setShapeDataRaw(null);
    setRecommendedStyles([]);
    setAvoidStyles([]);
    sessionSaveWarningShownRef.current = false;
    if (fileInputRef.current) fileInputRef.current.value = '';
    toast.success(t('admin.haircuts_session_reset_success'));
  };

  // Client & Image
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [showClientRequiredModal, setShowClientRequiredModal] = useState(false);
  const [sourceImage, setSourceImage] = useState(null);
  const [sourceImageFile, setSourceImageFile] = useState(null);

  // Generation
  const [generating, setGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [usedPrompt, setUsedPrompt] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('');
  const [variationCount, setVariationCount] = useState(0);
  const [mobileResultGlow, setMobileResultGlow] = useState(false);
  const [mobileSuccessPulse, setMobileSuccessPulse] = useState(false);
  const [mobileHighlightTarget, setMobileHighlightTarget] = useState(null);
  const [generationPhase, setGenerationPhase] = useState(0);
  const [sessionHydrated, setSessionHydrated] = useState(false);

  // Analysis
  const [analysisData, setAnalysisData] = useState(null);
  const [recommendedStyles, setRecommendedStyles] = useState([]);
  const [avoidStyles, setAvoidStyles] = useState([]);
  // Raw shape data used for saving to client & scan creation
  const [shapeDataRaw, setShapeDataRaw] = useState(null);

  // Face Analysis (new for paid face detection)
  const [detectedFaceShape, setDetectedFaceShape] = useState(null);
  const [isAnalyzingFace, setIsAnalyzingFace] = useState(false);

  // ===== EFFECTS =====
  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      const savedSession = loadHaircutsStudioSession();
      if (!savedSession) {
        setSessionHydrated(true);
        return;
      }

      setSelectedClient(savedSession.selectedClient || null);
      setGender(savedSession.gender || 'men');
      setSelectedPreset(savedSession.selectedPreset || null);
      setSelectedColorId(savedSession.selectedColorId || 'no-change');
      setUserCustomPrompt(savedSession.userCustomPrompt || '');
      setSelectedStyle(savedSession.selectedStyle || '');
      setSourceImage(savedSession.sourceImage || null);
      setGeneratedImage(savedSession.generatedImage || null);
      setUsedPrompt(savedSession.usedPrompt || '');
      setVariationCount(savedSession.variationCount || (savedSession.generatedImage ? 1 : 0));
      setAnalysisData(savedSession.analysisData || null);
      setRecommendedStyles(savedSession.recommendedStyles || []);
      setAvoidStyles(savedSession.avoidStyles || []);
      setShapeDataRaw(savedSession.shapeDataRaw || null);
      setDetectedFaceShape(savedSession.detectedFaceShape || savedSession.analysisData?.faceShape || null);

      if (savedSession.sourceImage) {
        const restoredFile = await dataUrlToFile(
          savedSession.sourceImage,
          savedSession.sourceImageFileName || 'haircuts-studio-photo.jpg'
        );
        if (!cancelled) {
          setSourceImageFile(restoredFile);
        }
      }

      if (!cancelled) {
        setSessionHydrated(true);
      }
    };

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!sessionHydrated) return;

    const hasSessionData =
      !!selectedClient ||
      !!sourceImage ||
      !!generatedImage ||
      !!selectedPreset ||
      !!selectedStyle ||
      !!userCustomPrompt.trim() ||
      selectedColorId !== 'no-change' ||
      gender !== 'men' ||
      !!analysisData ||
      !!detectedFaceShape;

    if (!hasSessionData) {
      clearHaircutsStudioSession();
      return;
    }

    const didSave = saveHaircutsStudioSession({
      selectedClient,
      gender,
      selectedPreset,
      selectedColorId,
      selectedStyle,
      userCustomPrompt,
      sourceImage,
      sourceImageFileName: sourceImageFile?.name || null,
      generatedImage,
      usedPrompt,
      variationCount,
      analysisData,
      recommendedStyles,
      avoidStyles,
      shapeDataRaw,
      detectedFaceShape,
    });

    if (!didSave && !sessionSaveWarningShownRef.current) {
      sessionSaveWarningShownRef.current = true;
      toast.error(t('admin.haircuts_session_save_failed'));
    }
  }, [
    analysisData,
    avoidStyles,
    detectedFaceShape,
    generatedImage,
    gender,
    recommendedStyles,
    selectedClient,
    selectedColorId,
    selectedPreset,
    selectedStyle,
    sessionHydrated,
    shapeDataRaw,
    sourceImage,
    sourceImageFile,
    t,
    usedPrompt,
    userCustomPrompt,
    variationCount,
  ]);

  // Filter presets when gender changes
  useEffect(() => {
    const presets = getPresetsByGender(gender);
    setFilteredPresets(presets);
  }, [gender]);

  useEffect(() => {
    const handleCreditsChanged = () => refetchCredits();
    window.addEventListener('credits:changed', handleCreditsChanged);
    return () => window.removeEventListener('credits:changed', handleCreditsChanged);
  }, [refetchCredits]);

  useEffect(() => {
    if (!isMobileViewport || !generatedImage || generating) return undefined;
    resultCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileSuccessPulse(true);
    const timer = window.setTimeout(() => setMobileSuccessPulse(false), 1800);
    return () => window.clearTimeout(timer);
  }, [generatedImage, generating, isMobileViewport]);

  const hasSelectedHairstyle = !!selectedPreset || !!selectedStyle || !!userCustomPrompt.trim();
  const mobileCanGenerate = !!selectedClient && !!sourceImageFile && hasSelectedHairstyle;
  const mobileHighlightClass = 'ring-2 ring-[#d4b47a] shadow-[0_0_24px_rgba(212,180,122,0.45)]';

  const scrollToMobileTarget = (targetRef, targetKey, message) => {
    if (!isMobileViewport) return false;
    targetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileHighlightTarget(targetKey);
    window.setTimeout(() => setMobileHighlightTarget(null), 1200);
    if (message) {
      toast.error(message);
    }
    return true;
  };

  const focusGeneratedResultOnMobile = () => {
    if (!isMobileViewport) return;
    resultCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileResultGlow(true);
    window.setTimeout(() => setMobileResultGlow(false), 1800);
  };

  // ===== HANDLERS =====
  const handleGenderChange = (newGender) => {
    setGender(newGender);
    setSelectedPreset(null);
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setShowClientSearch(false);
    toast.success(`Selected client: ${client.name}`);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if client is selected
    if (!selectedClient) {
      setShowClientRequiredModal(true);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setSourceImage(e.target.result);
      setSourceImageFile(file);
      toast.success('Image uploaded! Select a haircut to begin.');
    };
    reader.readAsDataURL(file);

    // DON'T show analysis on upload - only after generation
    // Reset analysis data to show empty state
    setAnalysisData(null);
    setShapeDataRaw(null);
    setRecommendedStyles([]);
    setAvoidStyles([]);
    setSelectedStyle('');

    // Clear file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePresetSelect = (preset) => {
    // Only update selection - does NOT trigger generation
    setSelectedPreset(preset);
    setSelectedStyle('');
    // Clear custom prompt when selecting a preset (either/or behavior)
    setUserCustomPrompt('');
    toast.success(`Selected: ${preset.name}`);
  };

  const handleCustomPromptChange = (value) => {
    setUserCustomPrompt(value);
    if (value.trim() && selectedStyle) {
      setSelectedStyle('');
    }
    // Clear preset selection when user starts typing (either/or behavior)
    if (value.trim() && selectedPreset) {
      setSelectedPreset(null);
      toast.info('Preset cleared - using custom request');
    }
  };

  const handleFaceAnalysis = async () => {
    if (!sourceImageFile) {
      toast.error('Please upload an image first');
      return;
    }

    if (isAnalyzingFace) {
      return; // Prevent repeated clicks
    }

    setIsAnalyzingFace(true);
    const loadingToast = toast.loading('Analyzing face shape...');

    try {
      // Call Face Analysis API (charges credits)
      const result = await hairstyleApiHelpers.postFaceAnalysis(sourceImageFile);

      const toNumberOrNull = (v) => {
        if (typeof v === 'number' && Number.isFinite(v)) return v;
        if (typeof v === 'string') {
          const n = parseFloat(v);
          return Number.isFinite(n) ? n : null;
        }
        return null;
      };

      // Normalize face shape
      const normalizedShape = normalizeFaceShape(result.faceShape);
      setDetectedFaceShape(normalizedShape);
      const normalizedHairProfile = normalizeHairProfile(result.hairProfile);
      const recommendations = getHaircutRecommendations({
        faceShape: normalizedShape,
        hairProfile: normalizedHairProfile,
      });
      const haircutStructure = buildHaircutStructure({
        faceShape: normalizedShape,
        hairProfile: normalizedHairProfile,
      });
      setRecommendedStyles(recommendations.recommendedStyles || []);
      setAvoidStyles(recommendations.avoidStyles || []);

      // Update analysis panel data
      setAnalysisData({
        faceShape: normalizedShape,
        metrics: {
          foreheadWidth: toNumberOrNull(result.measurements?.foreheadWidth),
          cheekboneWidth: toNumberOrNull(result.measurements?.cheekboneWidth),
          jawWidth: toNumberOrNull(result.measurements?.jawWidth),
          jawAngle: toNumberOrNull(result.measurements?.jawAngle),
        },
        hairProfile: normalizedHairProfile,
        haircutStructure,
        recommendedStyles: recommendations.recommendedStyles || [],
        avoidStyles: recommendations.avoidStyles || [],
      });

      toast.success(`Face shape detected: ${normalizedShape}`, { id: loadingToast });
      // Notify the topbar to refresh the credits badge (face analysis charges 1 credit)
      window.dispatchEvent(new Event('credits:changed'));
    } catch (error) {
      const errorMessage = handleHairstyleError(error);
      toast.error(errorMessage, { id: loadingToast });
    } finally {
      setIsAnalyzingFace(false);
    }
  };

  const handleSelectStyle = (style) => {
    // Latest user action wins: selecting a suggestion overrides any active preset.
    if (selectedPreset) {
      setSelectedPreset(null);
    }
    setSelectedStyle(style);
    const haircutStructure = buildHaircutStructure({
      faceShape: analysisData?.faceShape || detectedFaceShape || 'unknown',
      hairProfile: analysisData?.hairProfile || null,
    });
    setAnalysisData((prev) => ({
      ...(prev || {}),
      haircutStructure,
    }));
    if (userCustomPrompt.trim()) {
      setUserCustomPrompt('');
    }
    toast.success(`Selected style: ${style}`);
  };

  const handleGenerateClick = async () => {
    if (generating || isAnalyzingFace) {
      return; // Prevent repeated clicks
    }

    if (isMobileViewport) {
      if (!selectedClient) {
        setShowClientSearch(true);
        scrollToMobileTarget(clientSectionRef, 'client', t('admin.mobile_select_client_first'));
        return;
      }
      if (!sourceImageFile) {
        scrollToMobileTarget(uploadSectionRef, 'upload', t('admin.mobile_upload_photo_first'));
        return;
      }
      if (!hasSelectedHairstyle) {
        scrollToMobileTarget(styleSectionRef, 'style', t('admin.mobile_choose_hairstyle_first'));
        return;
      }
    }

    // Validation
    if (!sourceImageFile) {
      toast.error('Please upload an image first');
      return;
    }

    // Determine if we're in State A (nothing selected) or State B (something selected)
    const hasPreset = !!selectedPreset;
    const hasStyle = !!selectedStyle;
    const hasColor = selectedColorId && selectedColorId !== 'no-change';
    const hasCustom = !!userCustomPrompt.trim();
    const hasSelection = hasPreset || hasStyle || hasColor || hasCustom;

    // First click with no selection still performs analysis.
    if (!hasSelection && !analysisData) {
      await handleFaceAnalysis();
      return;
    }

    // Build a deterministic generation intent. If analysis already exists and user has
    // no explicit selection, we generate from a controlled structure fallback instead
    // of a vague generic haircut.
    const colorMode = selectedColorId === 'no-change' ? 'no_change' : 'preset';
    const selectedColor = getHairColorById(selectedColorId);
    const colorValue = selectedColor?.promptText;
    const effectiveFaceShape = analysisData?.faceShape || detectedFaceShape || 'unknown';
    const effectiveHairProfile = analysisData?.hairProfile || null;
    const effectiveHaircutStructure = analysisData?.haircutStructure || buildHaircutStructure({
      faceShape: effectiveFaceShape,
      hairProfile: effectiveHairProfile,
    });

    const generationIntent = resolveGenerationIntent({
      gender,
      preset: selectedPreset,
      selectedStyle,
      faceShape: effectiveFaceShape,
      hairProfile: effectiveHairProfile,
      haircutStructure: effectiveHaircutStructure,
      colorMode,
      colorValue,
      userCustomPrompt: userCustomPrompt.trim(),
    });
    setUsedPrompt(buildUserFriendlyPrompt({
      styleName: generationIntent.resolvedStyleName,
      styleDefinition: generationIntent.resolvedStyleDefinition,
      hairProfile: generationIntent.hairProfile,
    }));

    // Generate
    await generateHaircut(generationIntent);
  };

  const handleGeneratedImageReady = (imageUrl) => {
    setGeneratedImage(imageUrl);
    setVariationCount((currentCount) => currentCount + 1);
  };

  const generateHaircut = async (generationIntent) => {
    // Clear any lingering phase timers from a prior run
    generationPhaseTimersRef.current.forEach(id => window.clearTimeout(id));
    generationPhaseTimersRef.current = [];

    // Phase 1: upload complete, analyzing face active
    setGenerationPhase(1);
    setGenerating(true);
    setGeneratedImage(null);

    const loadingToast = toast.loading('Generating your new haircut...');

    try {
      if (isMobileViewport) {
        await waitForNextPaint();
        focusGeneratedResultOnMobile();
      }

      // Advance steps on a schedule independent of the API
      // so the user always sees at least some progress steps.
      generationPhaseTimersRef.current.push(
        window.setTimeout(() => setGenerationPhase(2), 1500)  // applying hairstyle
      );
      generationPhaseTimersRef.current.push(
        window.setTimeout(() => setGenerationPhase(3), 4000)  // rendering result
      );

      // Call AI generation API
      const payload = buildGenerationPayload({
        file: sourceImageFile,
        intent: generationIntent,
      });
      const result = await hairstyleApiHelpers.postGenerateHairstyle(payload);

      // API returned — clear the step timers and hold at "rendering" briefly
      // so the user never sees all steps green *before* the image appears.
      generationPhaseTimersRef.current.forEach(id => window.clearTimeout(id));
      generationPhaseTimersRef.current = [];
      setGenerationPhase(4); // rendering still active, not complete

      if (isMobileViewport) {
        await new Promise(resolve => window.setTimeout(resolve, 700));
      }

      setUsedPrompt(buildUserFriendlyPrompt({
        styleName: result.resolvedStyleName || generationIntent.resolvedStyleName,
        styleDefinition: result.resolvedStyleDefinition || generationIntent.resolvedStyleDefinition,
        hairProfile: result.gemini_analysis?.hair_profile || result.gemini_analysis?.hairProfile || generationIntent.hairProfile,
      }));

      // Check result status
      if (result.status === 'active' && result.output) {
        handleGeneratedImageReady(result.output);
        
        // Update analysis data with Gemini's facial analysis
        if (result.gemini_analysis) {
          const geminiAnalysis = result.gemini_analysis;
          const normalizedShape = normalizeFaceShape(geminiAnalysis.face_shape || 'unknown');
          
          const toNumberOrNull = (v) => {
            if (typeof v === 'number' && Number.isFinite(v)) return v;
            if (typeof v === 'string') {
              const n = parseFloat(v);
              return Number.isFinite(n) ? n : null;
            }
            return null;
          };

          setAnalysisData((prev) => {
            const resolvedHairProfile = normalizeHairProfile(
              geminiAnalysis.hair_profile || geminiAnalysis.hairProfile || prev?.hairProfile
            );
            const recommendations = getHaircutRecommendations({
              faceShape: normalizedShape,
              hairProfile: resolvedHairProfile,
            });
            setRecommendedStyles(recommendations.recommendedStyles || []);
            setAvoidStyles(recommendations.avoidStyles || []);

            return {
              faceShape: normalizedShape,
              metrics: {
                foreheadWidth: toNumberOrNull(geminiAnalysis.forehead_width),
                cheekboneWidth: toNumberOrNull(geminiAnalysis.cheekbone_width),
                jawWidth: toNumberOrNull(geminiAnalysis.jaw_width),
                jawAngle: toNumberOrNull(geminiAnalysis.jaw_angle),
              },
              hairProfile: resolvedHairProfile,
              haircutStructure: prev?.haircutStructure || buildHaircutStructure({
                faceShape: normalizedShape,
                hairProfile: resolvedHairProfile,
              }),
              recommendedStyles: recommendations.recommendedStyles || [],
              avoidStyles: recommendations.avoidStyles || [],
            };
          });
          
          // Also update shapeDataRaw for saving
          setShapeDataRaw({
            shape: normalizedShape,
            gemini_analysis: geminiAnalysis,
            hair_profile: geminiAnalysis.hair_profile || geminiAnalysis.hairProfile || null,
            metrics: {
              norm_forehead_width: geminiAnalysis.forehead_width,
              norm_cheekbone_width: geminiAnalysis.cheekbone_width,
              norm_jaw_width: geminiAnalysis.jaw_width,
              jaw_angle_mean: geminiAnalysis.jaw_angle,
            },
          });

          // Keep detected face shape in sync after generation.
          setDetectedFaceShape(normalizedShape);
        }
        
        toast.success('Haircut generated successfully!', { id: loadingToast });
        // Notify the topbar to refresh the credits badge immediately
        window.dispatchEvent(new Event('credits:changed'));

        // Auto-save to client's history (Analysis page & Client view)
        if (selectedClient && user) {
          try {
            // IMPORTANT: Use a consistent payload shape so downstream pages work reliably.
            const analysisPayload = {
              shapeData: shapeDataRaw || null,
              result: {
                ...result,
                // Ensure what's saved/visible matches what we actually sent
                promptUsed: result.promptUsed || result.prompt || generationIntent.previewPrompt,
              },
              analysisTime: null,
              confidence: 'High',
              generationTime: null,
              selectedStyleIndex: null,
              customPrompt: userCustomPrompt || '',
              originalImageUrl: sourceImage, // data URL created on upload (required for scan record creation)
            };

            await updateClientWithHairstyleAnalysis(selectedClient.id, analysisPayload);

            toast.success(`Saved to ${selectedClient.name}'s profile`, { duration: 2000 });
          } catch (saveError) {
            toast.error('Generated successfully but failed to save to client profile', { duration: 3000 });
          }
        }
      } else if (result.status === 'failed') {
        toast.error(result?.error || 'Generation failed. Please try again.', { id: loadingToast });
      } else if (result.status === 'init') {
        toast.error('Generation not ready. Please try again.', { id: loadingToast });
      } else if (result.output) {
        // Fallback: if there's an output regardless of status
        handleGeneratedImageReady(result.output);
        setUsedPrompt(buildUserFriendlyPrompt({
          styleName: result.resolvedStyleName || generationIntent.resolvedStyleName,
          styleDefinition: result.resolvedStyleDefinition || generationIntent.resolvedStyleDefinition,
          hairProfile: result.gemini_analysis?.hair_profile || result.gemini_analysis?.hairProfile || generationIntent.hairProfile,
        }));
        toast.success('Haircut generated successfully!', { id: loadingToast });
        window.dispatchEvent(new Event('credits:changed'));
      } else {
        throw new Error('No output received from AI service');
      }
    } catch (error) {
      generationPhaseTimersRef.current.forEach(id => window.clearTimeout(id));
      generationPhaseTimersRef.current = [];
      const errorMessage = handleHairstyleError(error);
      toast.error(errorMessage, { id: loadingToast });
    } finally {
      setGenerationPhase(0);
      setGenerating(false);
    }
  };

  const handleDownload = async (imageUrl) => {
    try {
      toast.loading('Downloading image...');
      
      // Fetch the image as blob
      const response = await fetch(imageUrl, { mode: 'cors' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      
      // Create object URL from blob
      const blobUrl = URL.createObjectURL(blob);
      
      // Create download link WITHOUT target="_blank" (this was opening new tab)
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `haircut-${selectedClient?.name?.replace(/\s+/g, '-') || 'result'}-${Date.now()}.jpg`;
      a.style.display = 'none';
      
      // Add to document, click, and remove
      document.body.appendChild(a);
      a.click();
      
      // Cleanup after a short delay
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      }, 100);
      
      toast.dismiss();
      toast.success('Image downloaded successfully!');
      
    } catch (error) {
      toast.dismiss();
      toast.error('Download failed. Please try again or right-click the image to save.');
    }
  };

  return (
    <div className="relative min-h-screen pb-32 md:pb-6">
      {/* Brand Glass Morphism Background Elements - Hidden in light mode */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden dark:block">
        <div className="absolute top-10 left-10 w-96 h-96 bg-gradient-to-br from-[#7d6633]/25 to-[#5a4d26]/20 rounded-full blur-3xl"></div>
        <div className="absolute top-32 right-16 w-80 h-80 bg-gradient-to-br from-[#a0824a]/20 to-[#7d6633]/25 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-1/3 w-72 h-72 bg-gradient-to-br from-[#d4b47a]/15 to-[#a0824a]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-32 right-20 w-64 h-64 bg-gradient-to-br from-[#7d6633]/30 to-[#d4b47a]/15 rounded-full blur-3xl"></div>
      </div>

      <div className="relative space-y-6 z-10">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center text-zinc-900 dark:text-zinc-100">
                <Scissors className="h-6 w-6 mr-2" style={{ color: ACCENT }} />
                {t('admin.haircuts_studio_title')}
              </h1>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
                {t('admin.haircuts_studio_subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Main Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* LEFT PANEL - Presets & Colors */}
          <div className="lg:col-span-3 space-y-4 order-2 lg:order-1">
            
            {/* Gender Toggle */}
            <Card>
              <CardHeader
                title={t('admin.section_gender_selection')}
                subtitle={t('admin.section_gender_choose')}
                icon={<Scissors className="h-5 w-5" style={{ color: ACCENT }} />}
              />
              <div className="p-4">
                <GenderToggle
                  value={gender}
                  onChange={handleGenderChange}
                  disabled={generating}
                />
              </div>
            </Card>

            {/* Haircut Presets */}
            <Card
              ref={styleSectionRef}
              className={`transition-all duration-300 scroll-mt-4 ${
                mobileHighlightTarget === 'style' ? mobileHighlightClass : ''
              }`}
            >
              <CardHeader
                title={t('admin.section_haircut_styles')}
                subtitle={`${filteredPresets.length} available for ${gender}`}
                icon={<Scissors className="h-5 w-5" style={{ color: ACCENT }} />}
              />
              <div className="p-4">
                <HaircutPresetPanel
                  presets={filteredPresets}
                  selectedGender={gender}
                  onPresetSelect={handlePresetSelect}
                  disabled={generating || userCustomPrompt.trim() !== ''}
                  loading={false}
                  selectedPresetId={selectedPreset?.id}
                />
                {userCustomPrompt.trim() && (
                  <p className="text-xs text-orange-600 dark:text-orange-400 mt-2 text-center">
                    {t('admin.using_custom_request')}
                  </p>
                )}
              </div>
            </Card>

            {/* Hair Color Selector */}
            <Card>
              <CardHeader
                title={t('admin.section_hair_color')}
                subtitle={t('admin.hair_color_optional')}
                icon={<div className="text-lg">🎨</div>}
              />
              <div className="p-4">
                <HairColorSelector
                  colors={hairColorOptions}
                  selectedColorId={selectedColorId}
                  onColorSelect={(colorId) => {
                    try {
                      setSelectedColorId(colorId);
                    } catch (error) {
                      console.error('Color selection error:', error);
                      toast.error('Failed to select color');
                    }
                  }}
                  disabled={generating || isAnalyzingFace}
                />
              </div>
            </Card>

            {/* Reset Button */}
            <Card>
              <div className="p-4">
                <motion.button
                  onClick={handleResetSession}
                  disabled={generating}
                  className="w-full py-2.5 px-4 rounded-lg border-2 border-zinc-300 dark:border-zinc-600
                           bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300
                           hover:bg-zinc-50 dark:hover:bg-zinc-700 hover:border-zinc-400 dark:hover:border-zinc-500
                           disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all duration-200 text-sm font-medium
                           flex items-center justify-center gap-2"
                  whileHover={!generating ? { scale: 1.01 } : {}}
                  whileTap={!generating ? { scale: 0.99 } : {}}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {t('admin.start_new_preview')}
                </motion.button>
              </div>
            </Card>

          </div>

          {/* CENTER PANEL - Before/After Viewer */}
          <div className="lg:col-span-6 space-y-4 order-3 lg:order-2">
            
            {/* Generated Result Display */}
            <Card
              ref={resultCardRef}
              className={`transition-all duration-500 scroll-mt-4 ${
                mobileResultGlow ? mobileHighlightClass : ''
              }`}
            >
              <CardHeader
                title={t('admin.section_generated_result')}
                subtitle={generatedImage ? t('admin.generated_result_preview') : t('admin.results_will_appear')}
                icon={<Scissors className="h-5 w-5" style={{ color: ACCENT }} />}
              />
              <div className="p-4">
                <BeforeAfterViewer
                  beforeImage={sourceImage}
                  afterImage={generatedImage}
                  loading={generating}
                  onDownload={handleDownload}
                  showSuccess={mobileSuccessPulse}
                  generationPhase={generationPhase}
                />
                {generatedImage && variationCount > 0 && (
                  <div className="md:hidden mt-3 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={handleGenerateClick}
                      disabled={generating || isAnalyzingFace}
                      className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#7d6633] px-3 py-3 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                      {t('admin.generate_variation')}
                    </button>
                    <button
                      type="button"
                      onClick={handleResetSession}
                      disabled={generating || isAnalyzingFace}
                      className="inline-flex items-center justify-center rounded-xl border border-zinc-200 bg-white px-3 py-3 text-xs font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                    >
                      {t('admin.start_new_preview')}
                    </button>
                  </div>
                )}
              </div>
            </Card>

            {/* Face Analysis - Mobile: immediately under generated image */}
            <div className="lg:hidden">
              <Card>
                <CardHeader
                  title={t('admin.section_face_analysis')}
                  subtitle={t('admin.face_ai_metrics')}
                  icon={<div className="text-lg">📊</div>}
                />
                <div className="p-4">
                  <AnalysisPanel
                    analysisData={analysisData}
                    recommendedStyles={recommendedStyles}
                    avoidStyles={avoidStyles}
                  />
                </div>
              </Card>
            </div>

            {/* Generate Button (PRIMARY CTA) - Professional Design with Dual State */}
            <Card className="hidden md:block">
              <div className="p-4">
                <motion.button
                  onClick={handleGenerateClick}
                  disabled={(generating || isAnalyzingFace) || !sourceImageFile}
                  className={`
                    w-full py-3.5 px-6 rounded-lg font-semibold text-base
                    transition-all duration-200 flex items-center justify-center gap-2.5
                    ${(generating || isAnalyzingFace) || !sourceImageFile
                      ? 'bg-zinc-300 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-500 cursor-not-allowed'
                      : 'bg-[#7d6633] hover:bg-[#6d5b2e] text-white shadow-md hover:shadow-lg'
                    }
                  `}
                  whileHover={!(generating || isAnalyzingFace) && sourceImageFile ? { scale: 1.01 } : {}}
                  whileTap={!(generating || isAnalyzingFace) && sourceImageFile ? { scale: 0.99 } : {}}
                >
                  {generating ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>{t('admin.generating_hairstyle_loading')}</span>
                    </>
                  ) : isAnalyzingFace ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>{t('admin.analyzing_face_loading')}</span>
                    </>
                  ) : (
                    <>
                      {(() => {
                        // Determine button state
                        const hasPreset = !!selectedPreset;
                        const hasStyle = !!selectedStyle;
                        const hasColor = selectedColorId && selectedColorId !== 'no-change';
                        const hasCustom = !!userCustomPrompt.trim();
                        const hasSelection = hasPreset || hasStyle || hasColor || hasCustom;

                        if (hasSelection) {
                          // STATE B: Generate Hairstyle
                          return (
                            <>
                              <Scissors className="h-5 w-5" />
                              <span>{t('admin.generate_hairstyle')}</span>
                            </>
                          );
                        } else {
                          // STATE A: Face Analysis
                          return (
                            <>
                              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span>{t('admin.ai_detect_face')}</span>
                            </>
                          );
                        }
                      })()}
                    </>
                  )}
                </motion.button>
                {!sourceImageFile && (
                  <p className="mt-2 text-xs text-center text-zinc-500 dark:text-zinc-400">
                    {t('admin.upload_first_to_generate')}
                  </p>
                )}
              </div>
            </Card>

            {/* Custom Prompt Input with Hair + Face Recommendations */}
            <Card className={generatedImage ? 'hidden md:block' : ''}>
              <CardHeader
                title={t('admin.section_custom_request')}
                subtitle={t('admin.custom_request_subtitle')}
                icon={<div className="text-lg">✍️</div>}
              />
              <div className="p-4">
                {/* Hair + Face recommendations (clickable) */}
                {detectedFaceShape && recommendedStyles.length > 0 && (
                  <div className="mb-4 space-y-2">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="h-6 w-6 rounded-full bg-[#7d6633]/10 flex items-center justify-center">
                        <svg className="h-4 w-4 text-[#7d6633]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <span className="text-xs font-semibold text-zinc-700 dark:text-zinc-300 uppercase tracking-wide">
                        {t('admin.detected_face')} {detectedFaceShape.charAt(0).toUpperCase() + detectedFaceShape.slice(1)} {t('admin.face_suffix')}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-600 dark:text-zinc-400 mb-2">
                      {t('admin.recommended_hairstyles_label')}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {recommendedStyles.map((style, index) => (
                        <motion.button
                          key={`${style}-${index}`}
                          onClick={() => handleSelectStyle(style)}
                          disabled={generating || isAnalyzingFace}
                          className={`w-full p-3 rounded-lg border text-left
                                   hover:bg-[#7d6633]/5 dark:hover:bg-[#7d6633]/20 hover:border-[#7d6633]/30
                                   transition-all duration-200
                                   disabled:opacity-50 disabled:cursor-not-allowed
                                   flex items-center justify-between gap-3 ${
                                     selectedStyle === style
                                       ? 'border-[#7d6633]/50 bg-[#7d6633]/8 dark:bg-[#7d6633]/25'
                                       : 'border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50'
                                   }`}
                          whileHover={!(generating || isAnalyzingFace) ? { scale: 1.01 } : {}}
                          whileTap={!(generating || isAnalyzingFace) ? { scale: 0.99 } : {}}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#7d6633]/10 text-[#7d6633] text-xs font-semibold flex-shrink-0">
                              {index + 1}
                            </span>
                            <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300 truncate">
                              {style}
                            </span>
                          </div>
                          {selectedStyle === style ? (
                            <span className="text-[11px] px-2 py-1 rounded-md bg-[#7d6633] text-white font-medium">{t('admin.style_selected')}</span>
                          ) : (
                            <svg className="h-4 w-4 text-zinc-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          )}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Custom Request Textarea */}
                <textarea
                  value={userCustomPrompt}
                  onChange={(e) => handleCustomPromptChange(e.target.value)}
                  placeholder={t('admin.custom_request_placeholder')}
                  disabled={generating || isAnalyzingFace}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 
                           bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 text-sm
                           focus:outline-none focus:ring-2 focus:ring-[#7d6633] focus:border-transparent
                           disabled:opacity-50 disabled:cursor-not-allowed resize-none"
                />
                {userCustomPrompt.trim() ? (
                  <p className="mt-2 text-xs text-orange-600 dark:text-orange-400 font-medium">
                    {t('admin.custom_request_override')}
                  </p>
                ) : (
                  <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
                    {t('admin.custom_request_added_haircut')}
                  </p>
                )}
              </div>
            </Card>

            {/* Used Prompt Display (Debug/Info) */}
            {usedPrompt && (
              <Card className={generatedImage ? 'hidden md:block' : ''}>
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-zinc-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-zinc-600 dark:text-zinc-400 uppercase tracking-wide mb-1">
                        {t('admin.hairstyle_applied')}
                      </p>
                      <pre className="text-xs text-zinc-500 dark:text-zinc-400 whitespace-pre-wrap font-mono bg-zinc-50 dark:bg-zinc-800/50 p-3 rounded-lg">
                        {usedPrompt}
                      </pre>
                    </div>
                  </div>
                </div>
              </Card>
            )}

          </div>

          {/* CLIENT & IMAGE SECTION - Mobile: Top, Desktop: Right */}
          <div className="lg:col-span-3 space-y-4 order-1 lg:order-3">
            
            {/* Client Selection & Upload */}
            <Card
              ref={clientSectionRef}
              className={`transition-all duration-300 scroll-mt-4 ${
                mobileHighlightTarget === 'client' ? mobileHighlightClass : ''
              }`}
            >
              <CardHeader
                title={t('admin.section_client_image')}
                subtitle={t('admin.client_select_first_subtitle')}
                icon={<Camera className="h-5 w-5" style={{ color: ACCENT }} />}
              />
              <div className="p-4 space-y-4">
                {/* Client Selection */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('admin.client_label')}</span>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowClientSearch(true)}
                      className="px-3 py-1.5 rounded-lg text-xs bg-[#7d6633] hover:bg-[#6d5b2e] text-white font-medium"
                    >
                      {selectedClient ? t('admin.client_change') : t('admin.client_select')}
                    </motion.button>
                  </div>
                  {selectedClient ? (
                    <div className="p-2 rounded-lg bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-white/10">
                      <div className="flex items-center gap-2">
                        <span className="text-[#7d6633]">👤</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">{selectedClient.name}</p>
                          {selectedClient.email && (
                            <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{selectedClient.email}</p>
                          )}
                        </div>
                        <span className="text-xs text-emerald-600">✅</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('admin.no_client_selected')}</p>
                  )}
                </div>

                {/* Upload Section */}
                <div
                  ref={uploadSectionRef}
                  className={`border-2 border-dashed p-4 rounded-xl bg-zinc-50 dark:bg-zinc-800/30 text-center border-[#7d6633]/30 transition-all duration-300 scroll-mt-4 ${
                    mobileHighlightTarget === 'upload' ? mobileHighlightClass : ''
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="hcs-file-upload"
                  />
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (!selectedClient) {
                          toast.error('Please select a client first');
                          setShowClientSearch(true);
                          return;
                        }
                        fileInputRef.current?.click();
                      }}
                      disabled={generating}
                      className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-lg w-full sm:w-auto ${
                        !selectedClient
                          ? 'bg-zinc-300 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 cursor-not-allowed'
                          : 'bg-[#7d6633] hover:bg-[#6d5b2e] text-white'
                      }`}
                    >
                      <Upload className="h-4 w-4" />
                      {t('admin.upload_photo')}
                    </button>

                    <CameraCapture
                      onFileSelect={handleFileSelect}
                      disabled={generating || !selectedClient}
                      className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-lg w-full sm:w-auto ${
                        !selectedClient
                          ? 'bg-zinc-300 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 cursor-not-allowed'
                          : 'bg-zinc-900 hover:bg-zinc-800 text-white'
                      }`}
                      buttonLabel={t('admin.camera_take_photo')}
                      facingMode="user"
                    />
                  </div>
                  <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">{t('admin.image_format_hint')}</p>
                  {!selectedClient && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">{t('admin.select_client_first_warning')}</p>
                  )}
                </div>

                {/* Source Image Thumbnail */}
                {sourceImage && (
                  <div className="relative aspect-square rounded-xl overflow-hidden bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-white/10">
                    <img
                      src={sourceImage}
                      alt="Source"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 left-2 px-2 py-1 rounded-lg bg-black/60 backdrop-blur-sm">
                      <span className="text-xs text-white font-medium">{t('admin.label_original')}</span>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* FACE ANALYSIS SECTION - Desktop: directly under Client & Image */}
            <div className="hidden lg:block">
              <Card>
                <CardHeader
                  title={t('admin.section_face_analysis')}
                  subtitle={t('admin.face_ai_metrics')}
                  icon={<div className="text-lg">📊</div>}
                />
                <div className="p-4">
                  <AnalysisPanel
                    analysisData={analysisData}
                    recommendedStyles={recommendedStyles}
                    avoidStyles={avoidStyles}
                  />
                </div>
              </Card>
            </div>

          </div>

        </div>

      </div>

      {!generatedImage && (
        <div
          className="md:hidden fixed inset-x-0 z-30 px-4 pointer-events-none"
          style={{ bottom: 'calc(112px + env(safe-area-inset-bottom, 0px))' }}
        >
          <motion.button
            type="button"
            onClick={handleGenerateClick}
            disabled={generating || isAnalyzingFace}
            className="pointer-events-auto w-full rounded-xl bg-[#7d6633] px-4 py-3.5 text-sm font-semibold text-white shadow-xl transition-colors disabled:cursor-not-allowed disabled:bg-zinc-300 disabled:text-zinc-600 dark:disabled:bg-zinc-800 dark:disabled:text-zinc-400 flex items-center justify-center gap-2"
            whileTap={{ scale: 0.98 }}
          >
            {isAnalyzingFace ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{t('admin.analyzing_face_loading')}</span>
              </>
            ) : (
              <>
                <Scissors className="h-4 w-4" />
                <span>{t('admin.generate_preview')}</span>
              </>
            )}
          </motion.button>
        </div>
      )}

      {/* Client Search Modal */}
      <ClientSearchModal
        isOpen={showClientSearch}
        onClose={() => setShowClientSearch(false)}
        onClientSelect={handleClientSelect}
        isDark={isDark}
      />

      {/* Client Required Modal */}
      {showClientRequiredModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6 backdrop-blur-sm">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="max-w-md w-full"
          >
            <Card>
              <div className="p-6 md:p-8">
                {/* Icon */}
                <div className="flex justify-center mb-4">
                  <div className="h-16 w-16 rounded-full bg-[#7d6633]/10 flex items-center justify-center">
                    <Info className="h-8 w-8 text-[#7d6633]" />
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-center mb-2 text-zinc-900 dark:text-zinc-100">
                  {t('admin.client_selection_required')}
                </h3>

                {/* Message */}
                <p className="text-center text-zinc-600 dark:text-zinc-400 mb-6">
                  {t('admin.client_selection_required_msg')}
                </p>

                {/* Actions */}
                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowClientRequiredModal(false);
                      setShowClientSearch(true);
                    }}
                    className="flex-1 px-4 py-3 rounded-xl text-white font-medium flex items-center justify-center gap-2 transition-all duration-200 hover:shadow-lg bg-[#7d6633] hover:bg-[#6d5b2e]"
                  >
                    {t('admin.select_client')}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowClientRequiredModal(false)}
                    className="flex-1 px-4 py-3 rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-800/50 text-zinc-700 dark:text-zinc-300 font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition-all"
                  >
                    {t('admin.cancel')}
                  </motion.button>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      )}

    </div>
  );
};

export default HaircutsStudio;

